import { Box } from '@primer/react';
import { isGroupfolioEntityType } from '@upright/common/model/input/groupfolios/types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { toImpactScoreIVKey } from '@upright/common/model/output/scores/types';
import { Valence } from '@upright/common/model/types';
import { getImpactBarDataLabelId } from '@upright/test-ids/dom-elements';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { valenceTextColorPrimer } from '@upright/ui/common/generic/utilities/format';
import { valenceToSide } from '@upright/ui/common/upright/core/impact-lens-profile/common/utils';
import { useImpactBarsLayout } from '@upright/ui/common/upright/core/impact-lens-profile/context';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { formatImpactScore } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-formatter';
import { isNil } from 'lodash';
import styled from 'styled-components';
import { formatPercentileIntoTopPercentByValence } from '../../common/utils';

interface Props {
  impact: ImpactCategoryId;
  valence: Valence;
}

/**
 * Data label drawn outside the bar
 */
export function ImpactBarOutsideDataLabel(props: Props) {
  const { valence, impact } = props;
  const context = useNetImpactLensContext();
  const layout = useImpactBarsLayout();

  const data = context?.data?.scores;
  if (!data || !context.data) {
    return <></>;
  }
  const { formatters, scoreUnit } = context.options;
  const value = context.options.scoreReader.readScore(impact, undefined, undefined, valence);
  // A ghost-baseline tail (see `NetImpactLensData.ghostScores`) keeps the
  // label visible even at score 0 — a ghost-only bar without its "+0.0" would
  // read as if the tail were the score.
  const ghostTailArea = layout.impactGhostTailArea(valenceToSide(valence), impact);
  if (!value && !ghostTailArea) {
    return <></>;
  }

  if (valence === Valence.OTHER) {
    return <></>;
  }
  const topImpactData = context.data.topImpactData?.[valence];
  const shouldHighlightLargest = topImpactData?.largestImpact?.impactCode === impact;
  const shouldHighlightMostDistinct = topImpactData?.mostDistinctImpact?.impactCode === impact;
  const percentileMap = context.data.topImpactData?.distinctnessData;
  const impactScoreIVKey = toImpactScoreIVKey({ valence, impactCode: impact });
  const percentile = impactScoreIVKey ? percentileMap?.get(impactScoreIVKey)?.percentile : undefined;

  const margin = 4;
  const labelArea = layout.impactBarLabelArea(valenceToSide(valence), impact);
  const barArea = layout.impactBarArea(valenceToSide(valence), impact);

  const x = valence === Valence.POSITIVE ? labelArea.x1 + margin : labelArea.x2 - margin;
  const anchor = valence === Valence.POSITIVE ? 'start' : 'end';

  if (barArea.width < 0.5 && !ghostTailArea) {
    return <></>;
  }
  const formattedScore = formatImpactScore(value || 0, formatters, scoreUnit);
  const color = BouColor.PLATFORM_TEXT_GRAY;

  const testId = getImpactBarDataLabelId(impact, valence);

  const showPercentiles = context.state.showCategoryPercentiles;

  const shouldShowHighlights = isGroupfolioEntityType(context.data.entity?.type);

  if (shouldShowHighlights && (shouldHighlightLargest || shouldHighlightMostDistinct)) {
    return (
      <Box
        as="text"
        x={x}
        textAnchor={anchor}
        y={labelArea.cy}
        dy="0.35em"
        data-testid={testId}
        sx={{
          fill: valenceTextColorPrimer(valence),
          fontWeight: 'bold',
        }}
      >
        {valence === Valence.POSITIVE ? '+' : '-'}
        {formattedScore}
        {!isNil(percentile) && ` (top ${formatPercentileIntoTopPercentByValence(percentile, valence)})`}
      </Box>
    );
  }

  return (
    <Text fill={color} x={x} textAnchor={anchor} y={labelArea.cy} dy="0.35em" data-testid={testId}>
      {valence === Valence.POSITIVE ? '+' : '-'}
      {formattedScore}
      {showPercentiles &&
        !isNil(percentile) &&
        ` (top ${formatPercentileIntoTopPercentByValence(percentile, valence)})`}
    </Text>
  );
}

const Text = styled.text``;
